import { useLayoutEffect, useRef, useState } from 'react';
import { formatNumber } from '../lib/utils';
import type { Locale } from '@worldcoo/monolith/core';

/**
 * Animated number counter using rAF with time-based exponential decay.
 *
 * A frozen `initialText` (useState with no setter) is rendered as children
 * so the element has correct dimensions on first paint / SSR. React sees the
 * same string every render, so it never overwrites ref-managed textContent.
 *
 * IMPORTANT: the mount branch must NOT call el.textContent — React already
 * rendered {initialText}. Touching textContent on mount detaches React's
 * text node and causes a blank frame on the next reconciliation.
 *
 * Deliberately NOT `suppressHydrationWarning`: this text is a live counter, so a
 * server/client delta is possible in principle, but the one that actually
 * produced the React #418 traffic was a bug — the client seeded its locale from
 * a hard-coded 'es' and formatted English pages with the wrong separators
 * (TEC-3689, fixed in `router.tsx`). Suppressing here would have hidden that,
 * and would hide the next locale regression too. Let React report it.
 */

export function AnimateNumber({
	locale,
	value,
	suffix,
	testId,
	onAnimateComplete,
	decimalDigits = 0,
	className,
}: {
	locale: Locale;
	value: number;
	suffix?: string;
	testId?: string;
	onAnimateComplete?: () => void;
	decimalDigits?: number;
	className?: string;
}) {
	const ref = useRef<HTMLDivElement>(null);
	const rafRef = useRef(0);
	const currentRef = useRef(value ?? 0);
	const isFirstRef = useRef(true);
	const onCompleteRef = useRef(onAnimateComplete);
	onCompleteRef.current = onAnimateComplete;

	const fmt = (n: number) => `${formatNumber(locale, n, decimalDigits)}${suffix ? ` ${suffix}` : ''}`;
	const fmtRef = useRef(fmt);
	fmtRef.current = fmt;

	// Frozen text for React children — never changes, so React never
	// overwrites the ref-managed textContent. Reserves layout space on
	// first paint / SSR to avoid CLS.
	const [initialText] = useState(() => fmt(value ?? 0));

	useLayoutEffect(() => {
		const el = ref.current;
		if (!el) return undefined;

		// On mount, just record the value — React already rendered
		// {initialText} so the text node is correct. Do NOT touch
		// el.textContent here or React's fiber loses its reference.
		if (isFirstRef.current) {
			isFirstRef.current = false;
			currentRef.current = value ?? 0;
			return undefined;
		}

		const target = value ?? 0;
		const targetText = fmtRef.current(target);

		// `requestAnimationFrame` does not fire while the document is hidden, so in a background tab — a
		// link opened with middle-click or cmd-click, which is ordinary traffic — the loop below would never
		// advance past its first frame. The element would sit on the PRE-animation text, showing the old
		// figure while `data-value` already carries the new one, until the visitor focuses the tab.
		//
		// There is no animation to be seen in a hidden tab anyway, so settle on the target immediately and
		// skip the rAF loop entirely. `onAnimateComplete` still fires, exactly as on the "already at target"
		// early return below — the bump coordination in `portal-animate-number` waits on that callback, and
		// never receiving it would leave the shared release counter hanging.
		if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
			currentRef.current = target;
			el.textContent = targetText;
			onCompleteRef.current?.();
			return undefined;
		}

		// Show current value immediately (before first rAF) to prevent flash
		el.textContent = fmtRef.current(currentRef.current);

		let prev = 0;

		const tick = (now: number) => {
			// Use rAF timestamps exclusively — performance.now() and rAF
			// timestamps can diverge, making dt negative on the first tick.
			const dt = prev ? (now - prev) / 1000 : 0;
			prev = now;

			// Time-based exponential decay (speed 8 ≈ settles in ~0.6s)
			currentRef.current += (target - currentRef.current) * (1 - Math.exp(-8 * dt));

			const text = fmtRef.current(currentRef.current);

			// Stop as soon as formatted output matches target — avoids
			// unnecessary rAF ticks where only the float changes but the
			// visible text is already the final value.
			if (text === targetText) {
				currentRef.current = target;
				el.textContent = targetText;
				onCompleteRef.current?.();
				return;
			}

			// Skip DOM write when formatted text hasn't changed
			if (el.textContent !== text) {
				el.textContent = text;
			}

			rafRef.current = requestAnimationFrame(tick);
		};
		rafRef.current = requestAnimationFrame(tick);

		return () => cancelAnimationFrame(rafRef.current);
	}, [value]);

	return (
		<div
			ref={ref}
			className={`inline-block tracking-tight tabular-nums${className ? ` ${className}` : ''}`}
			data-testid={testId}
			data-value={value}
		>
			{initialText}
		</div>
	);
}
